import L from 'leaflet'
import type { LatLngLiteral, LatLngTuple } from 'leaflet'

interface LatLng {
  lat: number
  lng?: number
  lon: number
}

export class GPS {
  // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
  static readonly PI: number = 3.14159265358979324
  static readonly x_pi: number = (GPS.PI * 3000.0) / 180.0

  static delta(lat: number, lon: number): LatLng {
    const a = 6378245.0
    // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
    const ee = 0.00669342162296594323
    let dLat = this.transformLat(lon - 105.0, lat - 35.0)
    let dLon = this.transformLon(lon - 105.0, lat - 35.0)
    const radLat = (lat / 180.0) * GPS.PI
    let magic = Math.sin(radLat)
    magic = 1 - ee * magic * magic
    const sqrtMagic = Math.sqrt(magic)
    dLat = (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * GPS.PI)
    dLon = (dLon * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * GPS.PI)
    return { lat: dLat, lon: dLon, lng: dLon }
  }

  // WGS-84 to GCJ-02
  static gcj_encrypt(wgsLat: number, wgsLon: number, type?: boolean): LatLng {
    let delta: LatLng
    if (type) {
      return { lat: wgsLat, lon: wgsLon }
    } else if (!type || this.outOfChina(wgsLat, wgsLon)) {
      delta = this.delta(wgsLat, wgsLon)
      return { lat: wgsLat + delta.lat, lon: wgsLon + delta.lon }
    } else {
      delta = this.delta(wgsLat, wgsLon)
      return { lat: wgsLat + delta.lat, lon: wgsLon + delta.lon }
    }
  }

  static gcj_decrypt(gcjLat: number, gcjLon: number, type?: boolean): LatLng {
    let delta: LatLng
    if (type) {
      return { lat: gcjLat, lon: gcjLon }
    } else if (!type || this.outOfChina(gcjLat, gcjLon)) {
      delta = this.delta(gcjLat, gcjLon)
      return { lat: gcjLat - delta.lat, lon: gcjLon - delta.lon }
    } else {
      delta = this.delta(gcjLat, gcjLon)
      return { lat: gcjLat - delta.lat, lon: gcjLon - delta.lon }
    }
  }

  static gcj_decrypt_exact(gcjLat: number, gcjLon: number): LatLng {
    const initDelta = 0.01
    const threshold = 0.000000001
    let dLat = initDelta,
      dLon = initDelta
    let mLat = gcjLat - dLat,
      mLon = gcjLon - dLon
    let pLat = gcjLat + dLat,
      pLon = gcjLon + dLon
    let wgsLat,
      wgsLon,
      i = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      wgsLat = (mLat + pLat) / 2
      wgsLon = (mLon + pLon) / 2
      const tmp = this.gcj_encrypt(wgsLat, wgsLon)
      dLat = tmp.lat - gcjLat
      dLon = tmp.lon - gcjLon
      if (Math.abs(dLat) < threshold && Math.abs(dLon) < threshold) break

      if (dLat > 0) pLat = wgsLat
      else mLat = wgsLat
      if (dLon > 0) pLon = wgsLon
      else mLon = wgsLon

      if (++i > 10000) break
    }

    return { lat: wgsLat, lon: wgsLon }
  }

  static bd_encrypt(gcjLat: any, gcjLon: any): LatLng {
    const x = gcjLon,
      y = gcjLat
    const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * GPS.x_pi)
    const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * GPS.x_pi)
    const bdLon = z * Math.cos(theta) + 0.0065
    const bdLat = z * Math.sin(theta) + 0.006
    return { lat: bdLat, lon: bdLon }
  }

  static bd_decrypt(bdLat: number, bdLon: number): LatLng {
    const x = bdLon - 0.0065,
      y = bdLat - 0.006
    const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * GPS.x_pi)
    const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * GPS.x_pi)
    const gcjLon = z * Math.cos(theta)
    const gcjLat = z * Math.sin(theta)
    return { lat: gcjLat, lon: gcjLon }
  }

  static mercator_encrypt(wgsLat: number, wgsLon: number): LatLng {
    const x = (wgsLon * 20037508.34) / 180.0
    let y =
      Math.log(Math.tan(((90.0 + wgsLat) * GPS.PI) / 360.0)) / (GPS.PI / 180.0)
    y = (y * 20037508.34) / 180.0
    return { lat: y, lon: x }
  }

  static mercator_decrypt(mercatorLat: number, mercatorLon: number): LatLng {
    const x = (mercatorLon / 20037508.34) * 180.0
    let y = (mercatorLat / 20037508.34) * 180.0
    y =
      (180 / GPS.PI) *
      (2 * Math.atan(Math.exp((y * GPS.PI) / 180.0)) - GPS.PI / 2)
    return { lat: y, lon: x }
  }

  static distance(
    latA: number,
    lonA: number,
    latB: number,
    lonB: number
  ): number {
    const earthR = 6371
    const x =
      Math.cos((latA * GPS.PI) / 180.0) *
      Math.cos((latB * GPS.PI) / 180.0) *
      Math.cos(((lonA - lonB) * GPS.PI) / 180)
    const y =
      Math.sin((latA * GPS.PI) / 180.0) * Math.sin((latB * GPS.PI) / 180.0)
    let s = x + y
    if (s > 1) s = 1
    if (s < -1) s = -1
    const alpha = Math.acos(s)
    const distance = alpha * earthR
    return distance
  }

  static outOfChina(lat: number, lon: number): boolean {
    if (lon < 72.004 || lon > 137.8347) return true
    if (lat < 0.8293 || lat > 55.8271) return true
    return false
  }

  static transformLat(x: number, y: number): number {
    let ret =
      -100.0 +
      2.0 * x +
      3.0 * y +
      0.2 * y * y +
      0.1 * x * y +
      0.2 * Math.sqrt(Math.abs(x))
    ret +=
      ((20.0 * Math.sin(6.0 * x * GPS.PI) + 20.0 * Math.sin(2.0 * x * GPS.PI)) *
        2.0) /
      3.0
    ret +=
      ((20.0 * Math.sin(y * GPS.PI) + 40.0 * Math.sin((y / 3.0) * GPS.PI)) *
        2.0) /
      3.0
    ret +=
      (160.0 * Math.sin((y / 12.0) * GPS.PI) +
        320.0 * Math.sin((y * GPS.PI) / 30.0) * 2.0) /
      3.0

    return ret
  }

  static transformLon(x: number, y: number): number {
    let ret =
      300.0 +
      x +
      2.0 * y +
      0.1 * x * x +
      0.1 * x * y +
      0.1 * Math.sqrt(Math.abs(x))

    ret +=
      ((20.0 * Math.sin(6.0 * x * GPS.PI) + 20.0 * Math.sin(2.0 * x * GPS.PI)) *
        2.0) /
      3.0

    ret +=
      ((20.0 * Math.sin(x * GPS.PI) + 40.0 * Math.sin((x / 3.0) * GPS.PI)) *
        2.0) /
      3.0

    ret +=
      ((150.0 * Math.sin((x / 12.0) * GPS.PI) +
        300.0 * Math.sin((x / 30.0) * GPS.PI)) *
        2.0) /
      3.0

    return ret
  }

  // 将XYZ坐标转换为经纬度坐标
  static xyz2Latlng(x: number, y: number, z: number): L.LatLng {
    const EARTH_RADIUS = 6371000 // 使用实际地球半径或其他相关常数值替换
    const lng = (x / 2 ** z) * 360 - 180
    const lat =
      (180 * Math.atan(Math.sinh(Math.PI * (0.5 - (2 * y) / EARTH_RADIUS)))) /
      Math.PI

    return L.latLng(lat, lng)
  }

  // 根据地图参数将经纬度坐标转换为GPS坐标
  static latLngToGPSBymap(
    latLng:
      | LatLngLiteral
      | LatLngTuple
      | [number, number, number]
      | { lat: number; lng: number; alt?: number | undefined },
    map: { offsetC: boolean; mapType: string }
  ): L.LatLng {
    let latlng = L.latLng(latLng as any)

    if (map.offsetC || map.mapType === 'MT_ESRI') {
      const gcjLatlng = GPS.gcj_decrypt(latlng.lat, latlng.lng)
      latlng = L.latLng(gcjLatlng.lat, gcjLatlng.lon)
    }

    return latlng
  }

  // 根据地图参数将GPS坐标转换为经纬度坐标
  static gpsToLatLngBymap(
    latLng:
      | LatLngLiteral
      | LatLngTuple
      | [number, number, number]
      | { lat: number; lng: number; alt?: number | undefined },
    map: { offsetC: boolean; mapType: string }
  ): L.LatLng {
    let originalLatLng = L.latLng(latLng as any)

    if (map.offsetC || map.mapType === 'MT_ESRI') {
      // 如果需要将GPS坐标转换为带有偏移的坐标（即GCJ-02坐标），调用gcj_encrypt方法
      const encryptedLatLng = GPS.gcj_encrypt(
        originalLatLng.lat,
        originalLatLng.lng
      )
      originalLatLng = L.latLng(encryptedLatLng.lat, encryptedLatLng.lon)
    }

    return originalLatLng
  }
}
